
import React from 'react';
import { MessageType } from '@/types/chat';
import { Play, FileText, File, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MessageContentProps {
  text: string;
  type: MessageType;
  mediaUrl?: string;
  mediaName?: string;
  mediaDuration?: number;
  mediaSize?: string;
  isUserMessage: boolean;
}

const MessageContent: React.FC<MessageContentProps> = ({
  text,
  type,
  mediaUrl,
  mediaName,
  mediaDuration,
  mediaSize,
  isUserMessage
}) => {
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderMediaContent = () => {
    switch (type) {
      case 'image':
        return (
          <div className="mt-2 rounded-md overflow-hidden">
            <img 
              src={mediaUrl} 
              alt={mediaName || 'Image'} 
              className="max-w-full max-h-48 object-contain cursor-pointer hover:opacity-90"
              onClick={() => window.open(mediaUrl, '_blank')}
            />
            {mediaName && <p className="text-xs mt-1">{mediaName}</p>}
          </div>
        );
      
      case 'document':
        return (
          <div className="mt-2 flex items-center gap-2 p-2 rounded-md bg-background/70">
            <FileText className="h-8 w-8" />
            <div className="overflow-hidden flex-1">
              <p className="text-sm truncate">{mediaName || 'Document'}</p>
              {mediaSize && <p className="text-xs">{mediaSize}</p>}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="p-1"
              onClick={() => window.open(mediaUrl, '_blank')}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        );
      
      case 'voice':
        return (
          <div className="mt-2 w-full">
            <audio controls src={mediaUrl} className="max-w-full w-full h-10" />
            {mediaDuration && (
              <p className="text-xs mt-1">{formatDuration(mediaDuration)}</p>
            )}
          </div>
        );
      
      case 'video':
        return (
          <div className="mt-2 relative rounded-md overflow-hidden">
            <video 
              src={mediaUrl} 
              className="max-w-full cursor-pointer"
              poster={mediaDuration ? undefined : mediaUrl}
              controls
            />
            {mediaDuration && (
              <p className="text-xs mt-1">{formatDuration(mediaDuration)}</p>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      {text && <p className="text-sm break-words">{text}</p>}
      {mediaUrl && renderMediaContent()}
    </>
  );
};

export default MessageContent;
